import _ from 'lodash';
import { BaseTicketController } from '../../../../common/base/ticketController';
import { StandingOrderFormFields } from './standingOrders.constants';

const StandingOrderStatusNew = 'new';

export class StandingOrdersController extends BaseTicketController {
  constructor(
    controllerService,
    progressService,
    $scope,
    $q,
    ticketService,
    notificationService,
    receivingListStateName,
    standingOrderConflictModalService,
    dataParser,
    fileResponseService,
  ) {
    'ngInject';
    super({
      controllerService,
      progressService,
      $scope,
      $q,
      ticketService,
      notificationService,
      receivingListStateName,
      standingOrderConflictModalService,
      dataParser,
      fileResponseService,
    });
  }

  get acceptStandingOrderspecialDate() {
    return (
      this.ticket &&
      this.ticket.formValues &&
      this.ticket.formValues.acceptStandingOrderspecialDate
    );
  }

  get featureStandingOrdersDownload() {
    return this.isFeatureActive(this.FeatureNames.FF_STANDING_ORDERS_DOWNLOAD);
  }

  get featureAutoApplyStandingOrders() {
    return this.isFeatureActive(
      this.FeatureNames.FF_AUTO_APPLY_STANDING_ORDERS,
    );
  }

  checkModelChanged(standingOrder) {
    // can not be edited
    if (
      !this.featureAutoApplyStandingOrders ||
      standingOrder.type === 'scheduled'
    ) {
      return false;
    }

    const keysToCompare = [
      'name',
      'amount',
      'interval',
      'executionDay',
      'nextExecution',
      'lastExecution',
      'purpose',
      'mref',
      'creditorId',
      'mandateSignatureDate',
      'iban',
      'currency',
    ];
    const original = _.find(this.originalStandingOrders, {
      paymentPartnerId: standingOrder.paymentPartnerId,
    });

    return !_.isEqual(
      _.pick(_.omitBy(original, _.isNil), keysToCompare),
      _.pick(_.omitBy(standingOrder, _.isNil), keysToCompare),
    );
  }

  saveChanges(item) {
    this.isSaveButtonLoading = true;

    this.ticketService
      .updateStandingOrder(this.ticket.id, item)
      .then(() => {
        this.originalStandingOrders = angular.copy(this.standingOrders);
        this.notificationService.success('app.standingOrders.update.success');
      })
      .finally(() => {
        this.isSaveButtonLoading = false;
      });
  }

  $onInit() {
    super.$onInit();

    if (this.featureAddRemarkToStandingOrders) {
      this.remark = {};
      this.messages = {
        email: 'app.standingOrders.remark.error.invalid.email',
        required: 'app.standingOrders.remark.error.required',
      };
    }

    //transfer, scheduled, debit
    this.standingOrders = this.mapStandingOrders(
      this.standingOrdersAndAppointmentTransfers,
    );
    this.setupFormFields();
    this.originalStandingOrders = angular.copy(this.standingOrders);

    if (!this.standingOrders) {
      this.submit();
    }

    this.submitReady = this.standingOrderOpen();
  }

  setupFormFields() {
    this.fieldDefinitions = {};
    this.standingOrdersForm = {};
    this.standingOrders.forEach((standingOrder) => {
      this.fieldDefinitions[standingOrder.paymentPartnerId] =
        StandingOrderFormFields(
          standingOrder.type,
          this.featureAutoApplyStandingOrders,
        );
    });
    return this.fieldDefinitions;
  }

  handleStatusChange(item, newStatus) {
    item.legacyStatus = item.progressStatus;

    if (newStatus === 'transferred' && item.type !== 'scheduled') {
      this.isAutoapplyButtonLoading = true;
    }

    this.ticketService
      .updateStandingOrderStatus(this.ticket.id, item.paymentPartnerId, {
        progressStatus: newStatus,
      })
      .then((resp) => this.statusChangeSuccess(item, newStatus))
      .catch((err) => this.handleError(err, item, newStatus))
      .finally(() => {
        this.submitReady = this.standingOrderOpen();
      });
  }

  statusChangeSuccess(item, newStatus) {
    item.isOpen = false;
    item.legacyStatus = item.progressStatus;
    item.progressStatus = newStatus;
    this.isAutoapplyButtonLoading = false;

    const maybeNewItem = _.find(
      this.standingOrders,
      (order) => order.progressStatus === 'new',
    );

    if (maybeNewItem) {
      maybeNewItem.isOpen = true;
    }
    if (newStatus === 'transferred') {
      this.notificationService.success(
        'app.standingOrders.transferToAtruvia.success',
      );
    }
  }

  handleError(error, item, newStatus) {
    item.progressStatus = item.legacyStatus;
    this.isAutoapplyButtonLoading = false;

    if (error.status === 409 && error.data.status === 'CONFLICT') {
      const err = error.data.error || {};
      return this.standingOrderConflictModalService
        .openModal(err, {
          paymentPartnerId: item.paymentPartnerId,
          ticketId: this.ticket.id,
        })
        .result.then((result) => {
          const { approved, conflictSolution, errorReason } = result;

          if (!approved) return;

          this.isAutoapplyButtonLoading = true;

          if (conflictSolution === 'NEXT_EXECUTION_DATE') {
            return this.ticketService
              .forceStandingOrdersWithExDate(
                this.ticket.id,
                item.paymentPartnerId,
              )
              .then((resp) => this.statusChangeSuccess(item, newStatus))
              .catch((err) => this.handleError(err, item, newStatus));
          }
          if (errorReason === 'CROSS_BORDER_CONFLICT') {
            return this.ticketService
              .sendStandingOrderWithConfirmedCrossBorder(
                this.ticket.id,
                item.paymentPartnerId,
                conflictSolution,
              )
              .then((resp) => this.statusChangeSuccess(item, newStatus))
              .catch((err) => this.handleError(err, item, newStatus));
          }

          return this.ticketService
            .forceStandingOrdersUpdate(
              this.ticket.id,
              item.paymentPartnerId,
              errorReason,
            )
            .then((resp) => this.statusChangeSuccess(item, newStatus))
            .catch((err) => this.handleError(err, item, newStatus));
        });
    }
    if (
      error.status === 409 &&
      error.data.result &&
      error.data.result.reason === 'NEXT_EXECUTION_DAY_CONFLICT'
    ) {
      return;
    }

    return this.notificationService.error(error.data.error.conflict);
  }

  mapStandingOrders(standingOrders) {
    const dateValues = ['nextExecutionDate', 'lastExecutionDate'];
    let firstOpenOrders = true;
    const order = ['transfer', 'debit', 'scheduled'];
    standingOrders = _.sortBy(standingOrders, (item) =>
      order.indexOf(item.type),
    );

    return standingOrders.map((order) => {
      dateValues.forEach((key) => {
        if (order[key]) {
          order[key] = this.dataParser.modifyDate(order[key], 'mediumDate');
        }
      });

      if (!order.progressStatus) {
        order.progressStatus = StandingOrderStatusNew;
      }

      // Open the first one
      if (firstOpenOrders && order.progressStatus === StandingOrderStatusNew) {
        order.isOpen = true;
        firstOpenOrders = false;
      }

      return order;
    });
  }

  standingOrderOpen() {
    return !_.some(
      this.standingOrders,
      (s) => s.progressStatus === StandingOrderStatusNew,
    );
  }

  downloadStandingOrdersCsv() {
    this.ticketService
      .downloadStandingOrdersCsv(this.ticket.id)
      .then((file) => this.fileResponseService.saveAs(file, 'application/csv'))
      .busy(this, 'standingOrdersDownloading')
      .finally(() => {
        this.submitReady = true;
      });
  }

  downloadStandingOrdersJson() {
    this.ticketService
      .downloadStandingOrdersJson(this.ticket.id)
      .then((file) => this.fileResponseService.saveAs(file, 'application/json'))
      .busy(this, 'standingOrdersJsonDownloading');
  }

  submit() {
    // prevent user to submit twice
    this.submitReady = false;
    if (
      this.featureAddRemarkToStandingOrders &&
      this.hasRemark &&
      this.remark
    ) {
      this.remarkForm.$submitted = true;

      if (this.remarkForm.$valid) {
        this.ticketService.processStandingOrdersWithRemark(
          this.ticket.id,
          this.remark,
        );
      }

      return;
    }

    this.ticketService.processStandingOrders(this.ticket.id);
  }
}
